import type { Auction } from "@/services/types";
import type { LivePreview } from "@/utils/auction";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";

interface LiveCalculationPanelProps {
  auction: Auction;
  preview: LivePreview;
}

/**
 * The persistent calculation panel. Shows the full breakdown of the current
 * lowest bid and places the expected collection beside the payout so their
 * equality is visible at a glance — the organizer's sanity check before closing.
 * Any rounding remainder is shown as an explicit labelled line, never silently
 * absorbed.
 */
export function LiveCalculationPanel({ auction, preview }: LiveCalculationPanelProps) {
  const exact = preview.dividendRemainder === 0;
  return (
    <div className="rounded-lg border border-background-200 bg-background-50">
      <div className="border-b border-background-100 px-4 py-3">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-foreground-900">
          <i className="ri-calculator-line text-primary-600" />
          Live calculation
        </h3>
        <p className="mt-0.5 text-xs text-foreground-500">Reflects the current lowest bid</p>
      </div>
      <dl className="space-y-2 px-4 py-3 text-sm">
        <div className="flex items-center justify-between">
          <dt className="text-foreground-600">Pool this month</dt>
          <dd className="tnum font-medium text-foreground-900"><CurrencyDisplay amount={auction.pool} hideSymbol /></dd>
        </div>
        <div className="flex items-center justify-between">
          <dt className="text-foreground-600">Current lowest bid</dt>
          <dd className="tnum font-semibold text-accent-800"><CurrencyDisplay amount={auction.pool - preview.discount} hideSymbol /></dd>
        </div>
        <div className="flex items-center justify-between border-t border-background-100 pt-2">
          <dt className="text-foreground-600">Discount</dt>
          <dd className="tnum font-medium text-danger-600"><CurrencyDisplay amount={preview.discount} hideSymbol /></dd>
        </div>
        <div className="flex items-center justify-between">
          <dt className="text-foreground-600">Base installment</dt>
          <dd className="tnum font-medium text-foreground-900"><CurrencyDisplay amount={auction.monthlyInstallment} hideSymbol /></dd>
        </div>
        <div className="flex items-center justify-between">
          <dt className="text-foreground-600">Dividend / member (−)</dt>
          <dd className="tnum font-medium text-success-700">− <CurrencyDisplay amount={preview.perMemberDividend} hideSymbol /></dd>
        </div>
        {preview.commission > 0 ? (
          <>
            <div className="flex items-center justify-between">
              <dt className="text-foreground-600">Commission / member (+)</dt>
              <dd className="tnum font-medium text-primary-700">+ <CurrencyDisplay amount={preview.commissionPerMember} hideSymbol /></dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-foreground-600">Total cycle commission</dt>
              <dd className="tnum font-medium text-primary-700"><CurrencyDisplay amount={preview.commission} hideSymbol /></dd>
            </div>
          </>
        ) : null}
        {preview.dividendRemainder > 0 ? (
          <div className="flex items-center justify-between rounded-md bg-warning-50 px-2 py-1">
            <dt className="text-warning-800">Rounding remainder</dt>
            <dd className="tnum font-medium text-warning-800"><CurrencyDisplay amount={preview.dividendRemainder} hideSymbol /></dd>
          </div>
        ) : null}
        <div className="flex items-center justify-between border-t border-background-100 pt-2">
          <dt className="text-foreground-600">Net installment / member</dt>
          <dd className="tnum text-lg font-bold text-foreground-950"><CurrencyDisplay amount={preview.netInstallment} hideSymbol /></dd>
        </div>
        <div className="flex items-center justify-between">
          <dt className="text-foreground-600">Total expected collection</dt>
          <dd className="tnum text-lg font-bold text-primary-700"><CurrencyDisplay amount={preview.totalCollected} hideSymbol /></dd>
        </div>
      </dl>

      <div className={`grid gap-2 px-4 pb-4 ${preview.commission > 0 ? "grid-cols-3" : "grid-cols-2"}`}>
        <div className="rounded-md bg-secondary-100 px-3 py-2.5 text-center">
          <p className="text-xs font-medium text-secondary-800">Collection</p>
          <p className="tnum mt-0.5 text-base font-bold text-secondary-900"><CurrencyDisplay amount={preview.totalCollected} hideSymbol /></p>
        </div>
        <div className="rounded-md bg-accent-100 px-3 py-2.5 text-center">
          <p className="text-xs font-medium text-accent-800">Winner prize</p>
          <p className="tnum mt-0.5 text-base font-bold text-accent-900">
            <CurrencyDisplay amount={auction.pool - preview.discount} hideSymbol />
          </p>
        </div>
        {preview.commission > 0 ? (
          <div className="rounded-md bg-primary-100 px-3 py-2.5 text-center">
            <p className="text-xs font-medium text-primary-800">Commission</p>
            <p className="tnum mt-0.5 text-base font-bold text-primary-900">
              <CurrencyDisplay amount={preview.commission} hideSymbol />
            </p>
          </div>
        ) : null}
      </div>
      <p className={`px-4 pb-4 text-xs ${exact ? "text-success-700" : "text-warning-700"}`}>
        {exact
          ? preview.commission > 0
            ? `Collection (${preview.totalCollected.toLocaleString("en-IN")}) equals Winner Prize (${(auction.pool - preview.discount).toLocaleString("en-IN")}) + Organizer Commission (${preview.commission.toLocaleString("en-IN")}).`
            : "Collection exactly equals the payout. Nothing is left over."
          : `A rounding remainder of ${(preview.dividendRemainder).toLocaleString("en-IN")} remains — collection is payout plus commission and remainder.`}
      </p>
    </div>
  );
}